// @file Stores that handles Plans and Upgrade modals
import { __ } from 'bits/intl'
import { Plan as PlanApi, Billing as BillingApi } from 'dashboard/padlet_api'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { EstimateSwitchingCostResult, Plan, User } from 'types'
import { Module } from 'vuex'
import getCsrfToken from 'bits/csrf_token'
import { navigateTo } from 'bits/location'
import { THINKING_FACE_ICON } from 'bits/confirmation_dialog'
import { ConfirmationDialogLayoutVariant } from 'vuexstore/modules/global_confirmation_dialog'
import { poll } from 'bits/polling'
import { isAppUsing } from 'bits/flip'
import { getUpgradeStepFromQuota, normalizedPaymentSchedule } from 'bits/billing_helper'
import { captureMessage } from 'bits/error_tracker'
import { FROZEN_FACE_ICON } from 'bits/emoji'

enum PadletQuota {
  Neon = 5,
  Gold = 20,
  Platinum = Infinity,
}

enum UpgradeStep {
  BelowPadletQuota = 'BelowPadletQuota',
  AtPadletQuota = 'AtPadletQuota',
  OverPadletQuota = 'OverPadletQuota',
  OverFileSizeQuota = 'OverFileSizeQuota',
  SchedulePro = 'SchedulePro',
  ConfirmProMonthly = 'ConfirmProMonthly',
  ConfirmProAnnual = 'ConfirmProAnnual',
  // New values for Gold plan feature
  ChooseTier = 'ChooseTier',
  ChooseTierPadletQuota = 'ChooseTierPadletQuota',
  ChooseTierFileSizeQuota = 'ChooseTierFileSizeQuota',
  ScheduleGold = 'ScheduleGold',
  SchedulePlatinum = 'SchedulePlatinum',
  EstimatePlatinumCost = 'EstimatePlatinumCost',
  ConfirmMonthlyPlatinum = 'ConfirmMonthlyPlatinum',
  ConfirmAnnualPlatinum = 'ConfirmAnnualPlatinum',
}

interface UpgradeModalState {
  xUpgradeModal: boolean
  upgradeStep: UpgradeStep | null
  upgradeError: string
  upgradeStatusCheckUrl: string
  isProcessingUpgrade: boolean
  estimatedUpgradeCost: EstimateSwitchingCostResult | null
  hasScheduledChanges: boolean
  isLoadingScheduledChangesInformation: boolean
  hasUserTriggeredUpgrade: boolean
}

interface PlansState extends UpgradeModalState {
  plans: {
    platinum: { annual: {} | Plan; monthly: {} | Plan }
    gold: { annual: {} | Plan; monthly: {} | Plan }
  }
}

const upgradeModalState: UpgradeModalState = {
  xUpgradeModal: false,
  upgradeStep: null,
  upgradeError: '',
  upgradeStatusCheckUrl: '',
  isProcessingUpgrade: false,
  estimatedUpgradeCost: null,
  hasScheduledChanges: false,
  isLoadingScheduledChangesInformation: false,
  hasUserTriggeredUpgrade: false,
}

const blankState = (): PlansState => ({
  plans: {
    platinum: { annual: {}, monthly: {} },
    gold: { annual: {}, monthly: {} },
  },
  ...upgradeModalState,
})

const PlansModule: Module<PlansState, object> = {
  namespaced: true,
  state: blankState,
  getters: {
    xUpgradeModal: (state) => state.xUpgradeModal,
    upgradeStep: (state) => state.upgradeStep,
    plans: (state) => state.plans,
    plansFetched: (state) => Object.keys(state.plans.platinum.annual).length !== 0,
    upgradeError: (state): string => state.upgradeError,
    upgradeStatusCheckUrl: (state): string => state.upgradeStatusCheckUrl,
    isProcessingUpgrade: (state): boolean => state.isProcessingUpgrade,
    estimatedUpgradeCost: (state): EstimateSwitchingCostResult | null => state.estimatedUpgradeCost,
    hasScheduledChanges: (state): boolean => state.hasScheduledChanges,
    isLoadingScheduledChangesInformation: (state): boolean => state.isLoadingScheduledChangesInformation,
    hasUserTriggeredUpgrade: (state): boolean => state.hasUserTriggeredUpgrade,
  },
  mutations: {
    FETCHED_PLANS(state, plans: Plan[]) {
      // Convert a list to a hash that is easy to use in our component
      const hashedPlans = {
        platinum: { annual: {}, monthly: {} },
        gold: { annual: {}, monthly: {} },
      }
      for (const plan of plans) {
        hashedPlans[plan.tier][plan.period] = plan
      }
      state.plans = hashedPlans
    },
    SHOW_PLAN_UPGRADE_MODAL(state, step: UpgradeStep): void {
      state.xUpgradeModal = true
      state.upgradeStep = step
    },
    CHANGE_STEP_PLAN_UPGRADE_MODAL(state, step: UpgradeStep): void {
      state.upgradeStep = step
    },
    CLOSE_PLAN_UPGRADE_MODAL(state): void {
      Object.assign(state, upgradeModalState)
    },
    SET_ESTIMATED_UPGRADE_COST(state, estimatedUpgradeCost: EstimateSwitchingCostResult): void {
      state.estimatedUpgradeCost = estimatedUpgradeCost
    },
    SET_UPGRADE_ERROR(state, upgradeError: string): void {
      state.upgradeError = upgradeError
    },
    SET_UPGRADE_STATUS_CHECK_URL(state, upgradeStatusCheckUrl: string): void {
      state.upgradeStatusCheckUrl = upgradeStatusCheckUrl
    },
    SET_IS_PROCESSING_UPGRADE(state, isProcessingUpgrade: boolean): void {
      state.isProcessingUpgrade = isProcessingUpgrade
    },
    SET_HAS_SCHEDULED_CHANGES(state, hasScheduledChanges: boolean): void {
      state.hasScheduledChanges = hasScheduledChanges
    },
    START_LOADING_SCHEDULED_CHANGES_INFORMATION(state): void {
      state.isLoadingScheduledChangesInformation = true
    },
    DONE_LOADING_SCHEDULED_CHANGES_INFORMATION(state): void {
      state.isLoadingScheduledChangesInformation = false
    },
    SET_HAS_USER_TRIGGERED_UPGRADE(state, hasUserTriggeredUpgrade: boolean): void {
      state.hasUserTriggeredUpgrade = hasUserTriggeredUpgrade
    },
  },
  actions: {
    async fetchPlans({ commit }): Promise<void> {
      const response = await PlanApi.fetch()
      commit('FETCHED_PLANS', response.data)
    },
    showPlanUpgradeModal({ commit }, step: UpgradeStep): void {
      commit('SHOW_PLAN_UPGRADE_MODAL', step)
    },
    showPlanUpgradeModalCheckingScheduledChanges(
      { commit, dispatch },
      { step, membershipTier, paymentSchedule },
    ): void {
      commit('SHOW_PLAN_UPGRADE_MODAL', step)
      if (membershipTier === 'gold') {
        dispatch('fetchScheduledChanges', { membershipTier, paymentSchedule })
      }
    },
    showPlanUpgradeToGoldModalCheckingQuota({ rootGetters, dispatch }): void {
      if (rootGetters.user.quota.walls_used > PadletQuota.Gold) {
        dispatch('showAboveQuotaOnUpgradeConfirmationDialog')
        return
      }
      dispatch('showPlanUpgradeToGoldModal')
    },
    userTriggeredUpgrade({ dispatch }, { user }: { user: User }): void {
      if (isAppUsing('goldPlansVisible')) {
        dispatch('showPlanUpgradeModalCheckingScheduledChanges', {
          step: UpgradeStep.ChooseTier,
          membershipTier: user.membership_tier,
          paymentSchedule: user.period,
        })
      } else {
        dispatch('showPlanUpgradeModal', getUpgradeStepFromQuota(user.quota))
      }
    },
    quotaTriggeredUpgrade({ dispatch }, { user }: { user: User }): void {
      if (isAppUsing('goldPlansVisible')) {
        dispatch('showPlanUpgradeModalCheckingScheduledChanges', {
          step: UpgradeStep.ChooseTierPadletQuota,
          membershipTier: user.membership_tier,
          paymentSchedule: user.period,
        })
      } else {
        dispatch('showPlanUpgradeModal', UpgradeStep.OverPadletQuota)
      }
    },
    showAboveQuotaOnUpgradeConfirmationDialog({ rootGetters, dispatch }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...THINKING_FACE_ICON,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('You’re above this tier’s padlet limit'),
          body: __(
            'You currently have %{amount} padlets, but our Gold tier only supports 20. You won’t be able to create new padlets until you are below the Gold tier limit. Consider switching to our Platinum tier instead.',
            { amount: rootGetters.user.quota.walls_used },
          ),
          confirmButtonText: __('Continue with Gold'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'plans/showPlanUpgradeToGoldModal' }],
        },
        { root: true },
      )
    },
    showPlanUpgradeToGoldModal({ commit }): void {
      commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.ScheduleGold)
    },
    closePlanUpgradeModal({ commit }): void {
      commit('CLOSE_PLAN_UPGRADE_MODAL')
    },
    async fetchEstimatedUpgradeCost({ commit, getters }, { paymentSchedule }): Promise<void> {
      const body = JSON.stringify({
        plan_id: getters.plans.platinum[normalizedPaymentSchedule(paymentSchedule)].id,
      })
      try {
        commit('SET_ESTIMATED_UPGRADE_COST', await BillingApi.estimateSwitchingCost({ body }))
      } catch {
        commit('SET_UPGRADE_ERROR', __("We're sorry, we were unable to process your request. Please try again later."))
      }
    },
    showUpgradeToPlatinumModal({ commit, dispatch }, { membershipTier, paymentSchedule }): void {
      if (membershipTier === 'neon') {
        commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.SchedulePlatinum)
      } else if (membershipTier === 'gold') {
        dispatch('showUpgradeFromGoldToPlatinumModal', { paymentSchedule })
      } else {
        captureMessage('User tried to upgrade to Platinum while on Platinum.')
      }
    },
    showUpgradeToPlatinumModalCheckingScheduledChanges(
      { commit, getters, dispatch },
      { membershipTier, paymentSchedule },
    ): void {
      if (membershipTier === 'neon') {
        commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.SchedulePlatinum)
        return
      }
      // If scheduled changes is still loading, we wait for that to finish first before `showUpgradeFromGoldToPlatinumModal`
      if (getters.isLoadingScheduledChangesInformation) {
        commit('SET_HAS_USER_TRIGGERED_UPGRADE', true)
        return
      }
      if (getters.hasScheduledChanges) {
        commit('CLOSE_PLAN_UPGRADE_MODAL')
        dispatch('triggerScheduledChangesConfirmationDialog')
        return
      }
      dispatch('changeStepUpgradeFromGoldToPlatinumModal', { paymentSchedule })
    },
    async showUpgradeFromGoldToPlatinumModal({ commit, dispatch }, { paymentSchedule }): Promise<void> {
      commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.EstimatePlatinumCost)
      await dispatch('fetchEstimatedUpgradeCost', { paymentSchedule })
      commit(
        'CHANGE_STEP_PLAN_UPGRADE_MODAL',
        paymentSchedule === 'month' ? UpgradeStep.ConfirmMonthlyPlatinum : UpgradeStep.ConfirmAnnualPlatinum,
      )
    },
    async changeStepUpgradeFromGoldToPlatinumModal({ commit, dispatch }, { paymentSchedule }): Promise<void> {
      commit('CHANGE_STEP_PLAN_UPGRADE_MODAL', UpgradeStep.EstimatePlatinumCost)
      await dispatch('fetchEstimatedUpgradeCost', { paymentSchedule })
      commit(
        'CHANGE_STEP_PLAN_UPGRADE_MODAL',
        paymentSchedule === 'month' ? UpgradeStep.ConfirmMonthlyPlatinum : UpgradeStep.ConfirmAnnualPlatinum,
      )
    },
    async upgradeToPlatinum({ getters, commit }, { paymentSchedule }): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_UPGRADE', true)
        const {
          links: { status },
        } = await fetchJson('/premium/switch_plan', {
          method: HTTPMethod.post,
          jsonData: {
            switch_type: 'switch_immediately',
            plan_id: getters.plans.platinum[normalizedPaymentSchedule(paymentSchedule)].id,
          },
          csrfToken: getCsrfToken(),
        })
        await poll({
          pollingUrl: status,
          validationCallback: (response) => response.switched,
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        navigateTo('/memberships/confirmation')
      } catch (e) {
        commit('SET_UPGRADE_ERROR', __(`We're sorry, we were unable to process your request. Please try again later.`))
      } finally {
        commit('SET_IS_PROCESSING_UPGRADE', false)
      }
    },
    async fetchScheduledChanges({ commit, dispatch }, { paymentSchedule }): Promise<void> {
      commit('START_LOADING_SCHEDULED_CHANGES_INFORMATION')
      try {
        const response = await BillingApi.fetchScheduledChanges()
        commit('SET_HAS_SCHEDULED_CHANGES', response.data.has_scheduled_changes)
        dispatch('handleScheduledChangesLoaded', { paymentSchedule })
      } finally {
        commit('DONE_LOADING_SCHEDULED_CHANGES_INFORMATION')
      }
    },
    handleScheduledChangesLoaded({ commit, getters, dispatch }, { paymentSchedule }): void {
      // If the user already triggered an upgrade from Gold to Platinum and is waiting for loading schedule changes to finish
      if (getters.hasUserTriggeredUpgrade) {
        commit('SET_HAS_USER_TRIGGERED_UPGRADE', false)
        if (getters.hasScheduledChanges) {
          commit('CLOSE_PLAN_UPGRADE_MODAL')
          dispatch('triggerScheduledChangesConfirmationDialog')
        } else {
          dispatch('changeStepUpgradeFromGoldToPlatinumModal', { paymentSchedule })
        }
      }
    },
    triggerScheduledChangesConfirmationDialog({ dispatch, rootGetters }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...FROZEN_FACE_ICON,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('Upgrade currently unavailable'),
          body: __(
            'We are unable to upgrade your membership due to changes that are pending in your account. You can check your membership status and make changes from the billing page in settings.',
          ),
          confirmButtonText: __('Go to billing'),
          cancelButtonText: __('Cancel'),
          afterConfirmActions: [{ type: 'plans/navigateToBillingSettingsPage' }],
          shouldFadeIn: false,
        },
        { root: true },
      )
    },
    navigateToBillingSettingsPage(): void {
      navigateTo('/dashboard/settings/billing')
    },
  },
}

export default PlansModule
export { PadletQuota, UpgradeStep }
